import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core'
import { GraphService } from '../graph.service';
import { Linqset } from '../../common/linqset-model';
import { NodeService } from '../../model-services/node.service';
import { CreateEdgeInputMode, EventRecognizers, ExteriorNodeLabelModel, GraphComponent, GraphEditorInputMode, GraphItemTypes, IModelItem, INode, InputModeEventArgs, ItemEventArgs, Point } from '@yfiles/yfiles';
import { v4 } from 'uuid';
import { LABEL_TYPES } from '../../model-services/model-item-enums';

@Component({
    selector: 'app-tutorial-graph-component',
    templateUrl: './tutorial-graph.component.html',
    styleUrls: ['./tutorial-graph.component.css'],
    standalone: false
})
export class TutorialGraphComponent implements AfterViewInit, OnDestroy {
    @Input() linqset:Linqset

    graphComponent!: GraphComponent
	mouseCurrentLocation:Point = null;

    @Output() itemsStartDragEvent = new EventEmitter<INode[]>();
    @Output() itemsDraggedEvent = new EventEmitter<INode[]>();
    @Output() itemCreatedEvent = new EventEmitter<IModelItem>();

    @ViewChild('tutorialGraphComponent') graphComponentRef: ElementRef | undefined
    
    constructor(private graphService:GraphService, private nodeService:NodeService) {
    }

    ngAfterViewInit(): void {
        if (this.graphComponentRef) {  
            this.graphComponent = new GraphComponent(this.graphComponentRef.nativeElement)   
            this.setupGraph()
        }
    }

    ngOnDestroy(): void {            
        this.removeListeners()
        if (this.graphComponent) this.graphComponent.cleanUp()

    }

    private setupGraph(): void {
        const inputMode = new GraphEditorInputMode({
            allowGroupingOperations: false,
            allowCreateNode: false,
            allowEditLabelOnDoubleClick: false,
            allowAddLabel: false,
            allowAdjustGroupNodeSize: false,
            allowCreateEdge:true,
            allowEditLabel: false,
            deletableItems: GraphItemTypes.NONE,
            ignoreVoidStyles: true
        })

        inputMode.selectablePredicate = (item) => {
            if (item.tag && !item.tag.disabledSelect) return true
            return false
        }

        inputMode.deletablePredicate = (item) => {
            return true
        }
        this.graphService.createGridLayout(this.graphComponent)
        
        this.graphComponent.inputMode = inputMode

        this.initializeStyling()
        this.createEdgeInputMode(inputMode)
        this.createNodeInputMode(inputMode)
        this.createListeners()
    }

    private createListeners() {
        const inputMode = <GraphEditorInputMode>this.graphComponent.inputMode
        inputMode.moveSelectedItemsInputMode.addEventListener('drag-started', this.handleDragStarted.bind(this))
        inputMode.moveSelectedItemsInputMode.addEventListener('drag-finishing', this.handleDroppedNode.bind(this))

        inputMode.createEdgeInputMode.addEventListener('gesture-started', this.handleGestureStarted.bind(this))

        this.graphComponent.graph.addEventListener('node-created', this.handleItemCreated.bind(this))
        this.graphComponent.graph.addEventListener('edge-created', this.handleItemCreated.bind(this))
    }

    private removeListeners() {
        const inputMode = <GraphEditorInputMode>this.graphComponent.inputMode
        if (inputMode) {
            inputMode.moveSelectedItemsInputMode.removeEventListener('drag-started', this.handleDragStarted.bind(this))
            inputMode.moveSelectedItemsInputMode.removeEventListener('drag-finishing', this.handleDroppedNode.bind(this))

            inputMode.createEdgeInputMode.removeEventListener('gesture-started', this.handleGestureStarted.bind(this))
            
            this.graphComponent.graph.removeEventListener('node-created', this.handleItemCreated.bind(this))
            this.graphComponent.graph.removeEventListener('edge-created', this.handleItemCreated.bind(this))

        }
    }

    private handleDroppedNode(evt:InputModeEventArgs, sender:any) {
        console.log("Dropped node")
        this.itemsDraggedEvent.emit(sender.affectedItems)
    }

    private handleDragStarted(evt:InputModeEventArgs, sender:any) {
        console.log("Drag started")
        this.itemsStartDragEvent.emit(sender.affectedItems)
    }

    private handleItemCreated(evt:ItemEventArgs<INode>, sender:any) {
        this.itemCreatedEvent.emit(evt.item)
    }

    private initializeStyling() {
        this.graphService.initializeStyling(this.graphComponent)
        this.graphService.createPortHandler(this.graphComponent, this.linqset)
    }

    private createNodeInputMode(inputMode: GraphEditorInputMode) {
        // Configure drag & drop
        const nodeDropInputMode = inputMode.nodeDropInputMode;
        // by default the mode available in GraphEditorInputMode is disabled, so first enable it
        nodeDropInputMode.enabled = true;
        nodeDropInputMode.showPreview = true;

        nodeDropInputMode.itemCreator = (
            context,
            graph,
            dropData,
            dropTarget,
            dropLocation
        ) => {
            var dropLocation = nodeDropInputMode.dropLocation;
            const linqsetNode = dropData.tag.node
            const node = this.nodeService.createNodeAt(this.linqset, graph, linqsetNode, v4(), dropLocation)
            node.tag.index = dropData.tag.index
            graph.addLabel(node, linqsetNode.name, ExteriorNodeLabelModel.BOTTOM, undefined, undefined, {type:LABEL_TYPES.NODE_NAME})
            return node
        }
       
        nodeDropInputMode.isValidParentPredicate = function(targetNode) {
            if (targetNode) return false
            return true
        };
    }

    private createEdgeInputMode(inputMode: GraphEditorInputMode) {
        const createEdgeInputMode = inputMode.createEdgeInputMode        
        createEdgeInputMode.allowCreateBend = false
        createEdgeInputMode.allowSelfLoops = false
        createEdgeInputMode.allowEdgeToEdgeConnections = false
        createEdgeInputMode.useHitItemsCandidatesOnly = true
        createEdgeInputMode.cancelRecognizer = (evt, sender) =>
            EventRecognizers.MOUSE_LEFT_CLICK(evt, sender) && EventRecognizers.ESCAPE_DOWN(evt, sender)

        createEdgeInputMode.edgeCreator = (
            context,
            graph,
            sourceCandidate,
            targetCandidate,
            dummyEdge
        ) => {
            if (targetCandidate.owner.tag.disabledEdge) return null
            return this.graphService.edgeCreator(this.graphComponent, this.linqset, context, sourceCandidate, targetCandidate, dummyEdge)
        }
    }
    
    private handleGestureStarted(evt:any, sender:CreateEdgeInputMode) {
        const sourcePort = sender.startPortCandidate
        if (sourcePort && sourcePort.owner && !sourcePort.owner.tag.disabledEdge)
            this.graphService.edgeCreationListener(this.graphComponent, sender)
        else sender.cancel()
    }    
}